import { useState, useCallback } from 'react';
import { Check, ChevronDown, Palette } from 'lucide-react';
import { Button } from '@/components/UI/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/UI/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/UI/dialog';
import { useEditorStore } from '../../store/editor-store';
import {
  CHART_PALETTE_OPTIONS,
  DEFAULT_CUSTOM_CHART_COLORS,
  type ChartPaletteId,
} from '../../utils/chart-palettes';
import { cn } from '@/lib/utils';

const CUSTOM_COLOR_COUNT = 8;

interface ChartColorSelectorProps {
  value?: ChartPaletteId;
  customColors?: string[];
  themeColors?: string[];
  className?: string;
  onValueChange?: (paletteId: ChartPaletteId) => void;
  onCustomColorsChange?: (colors: string[]) => void;
}

function ColorStrip({ colors, className }: { colors: string[]; className?: string }) {
  return (
    <span className={cn('inline-flex shrink-0 gap-0.5', className)}>
      {(colors || []).slice(0, 4).map((c, i) => (
        <span
          key={i}
          className="border-muted h-3 w-3 shrink-0 rounded-sm border"
          style={{ backgroundColor: c }}
          title={c}
        />
      ))}
    </span>
  );
}

export function ChartColorSelector({
  value,
  customColors,
  themeColors,
  className,
  onValueChange,
  onCustomColorsChange,
}: ChartColorSelectorProps = {}) {
  const storeChartPaletteId = useEditorStore((s) => s.themeState.chartPaletteId);
  const storeCustomChartColors = useEditorStore((s) => s.themeState.customChartColors);
  const setChartPalette = useEditorStore((s) => s.setChartPalette);
  const setCustomChartColors = useEditorStore((s) => s.setCustomChartColors);
  const chartColors = useEditorStore((s) => s.getChartColors());
  const chartPaletteId = value ?? storeChartPaletteId;
  const customChartColors = customColors ?? storeCustomChartColors;

  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [editingColors, setEditingColors] = useState<string[]>(() =>
    (customChartColors?.length ? customChartColors : DEFAULT_CUSTOM_CHART_COLORS).slice(0, CUSTOM_COLOR_COUNT)
  );

  const openCustomPicker = useCallback(() => {
    setEditingColors(
      (customChartColors?.length ? customChartColors : DEFAULT_CUSTOM_CHART_COLORS).slice(0, CUSTOM_COLOR_COUNT)
    );
    setShowCustomPicker(true);
  }, [customChartColors]);

  const handleSaveCustom = useCallback(() => {
    const toSave = [...editingColors];
    while (toSave.length < CUSTOM_COLOR_COUNT) toSave.push(DEFAULT_CUSTOM_CHART_COLORS[toSave.length] ?? '#64748b');
    if (onCustomColorsChange) {
      onCustomColorsChange(toSave);
    } else {
      setCustomChartColors(toSave);
    }
    if (onValueChange) {
      onValueChange('custom');
    } else {
      setChartPalette('custom');
    }
    setShowCustomPicker(false);
  }, [editingColors, onCustomColorsChange, onValueChange, setCustomChartColors, setChartPalette]);

  const currentOption = CHART_PALETTE_OPTIONS.find((o) => o.id === chartPaletteId);
  const displayColors =
    chartPaletteId === 'custom'
      ? (customChartColors?.length ? customChartColors : DEFAULT_CUSTOM_CHART_COLORS)
      : chartPaletteId === 'theme'
        ? (themeColors?.length ? themeColors : chartColors)
        : (currentOption?.colors ?? chartColors);
  const currentLabel = currentOption?.label ?? 'Custom';

  const getOptionColors = useCallback(
    (paletteId: ChartPaletteId, colors?: string[]) => {
      if (paletteId === 'theme') return themeColors?.length ? themeColors : chartColors;
      if (paletteId === 'custom') return customChartColors?.length ? customChartColors : DEFAULT_CUSTOM_CHART_COLORS;
      return colors ?? [];
    },
    [chartColors, customChartColors, themeColors]
  );

  const handlePaletteChange = useCallback(
    (paletteId: ChartPaletteId) => {
      if (onValueChange) {
        onValueChange(paletteId);
        return;
      }
      setChartPalette(paletteId);
    },
    [onValueChange, setChartPalette]
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn('group relative justify-between md:min-w-56', className)}
            title="Chart colors (bars & lines)"
            aria-label="Select chart color palette"
          >
            <div className="flex w-full items-center gap-3 overflow-hidden">
              <ColorStrip colors={displayColors} />
              <span className="truncate text-left font-medium capitalize">{currentLabel}</span>
            </div>
            <ChevronDown className="size-4 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          collisionPadding={16}
          className="min-w-[300px] max-h-[min(var(--radix-dropdown-menu-content-available-height),20rem)] overflow-y-auto p-1"
        >
          {CHART_PALETTE_OPTIONS.filter((o) => o.id !== 'custom').map((opt) => (
            <DropdownMenuItem
              key={opt.id}
              onClick={() => handlePaletteChange(opt.id as ChartPaletteId)}
              className="cursor-pointer flex items-center gap-2 py-2"
            >
              <ColorStrip colors={getOptionColors(opt.id as ChartPaletteId, opt.colors)} />
              <span className={cn('flex-1 text-sm font-medium capitalize', chartPaletteId === opt.id && 'font-semibold')}>
                {opt.label}
              </span>
              {chartPaletteId === opt.id && <Check className="h-4 w-4 shrink-0 opacity-70" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              openCustomPicker();
            }}
            className="cursor-pointer flex items-center gap-2 py-2"
          >
            {chartPaletteId === 'custom' && (customChartColors?.length ? customChartColors : DEFAULT_CUSTOM_CHART_COLORS).length > 0 ? (
              <ColorStrip
                colors={customChartColors?.length ? customChartColors : DEFAULT_CUSTOM_CHART_COLORS}
              />
            ) : (
              <span className="flex h-3 w-[54px] shrink-0 items-center justify-center rounded-sm border border-border/50 bg-muted/50">
                <Palette className="h-2.5 w-2.5 text-muted-foreground" />
              </span>
            )}
            <span className={cn('flex-1 text-sm font-medium capitalize', chartPaletteId === 'custom' && 'font-semibold')}>
              Custom
            </span>
            {chartPaletteId === 'custom' && <Check className="h-4 w-4 shrink-0 opacity-70" />}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showCustomPicker} onOpenChange={setShowCustomPicker}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Custom chart colors</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Choose colors for chart bars and lines. They will be saved and used until you change them.
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 py-2">
            {Array.from({ length: CUSTOM_COLOR_COUNT }, (_, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5">
                <input
                  type="color"
                  value={editingColors[i] ?? DEFAULT_CUSTOM_CHART_COLORS[i]}
                  onChange={(e) => {
                    const next = [...editingColors];
                    while (next.length <= i) next.push(DEFAULT_CUSTOM_CHART_COLORS[next.length] ?? '#64748b');
                    next[i] = e.target.value;
                    setEditingColors(next);
                  }}
                  className="w-10 h-10 rounded-lg border-2 border-border cursor-pointer bg-transparent"
                  title={`Color ${i + 1}`}
                />
                <span className="text-xs text-muted-foreground">Color {i + 1}</span>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowCustomPicker(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCustom}>
              Save & use for charts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
